import React from 'react';
import SignatureCanvas from 'react-signature-canvas';
import * as localForage from 'localforage';
import { Col, Button, FormGroup, FormLabel, FormControl } from 'react-bootstrap';
import { EditorState } from 'draft-js';
import { TFunction } from 'i18next';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import { toastr } from 'react-redux-toastr';
import Select from 'react-select';

import RichTextEditor from '../../components/common/RichTextEditor';
import { IsignatureFormData, IinstallBasePopulated, Ijob, IjobSignature, Iuser, IfacilityContact } from '../../models';
import { constants } from '../../constants/constants';
import { RequiredIndicator } from '../common/FormUtil';
import { jobTypesIdEnum, SignatureTypeEnum } from '../../models-enums';
import { toggleModalSignaturePad as ItoggleModalSignaturePad } from '../../actions/manageInventoryActions';
import { submitJobSignature, toggleIsCollectingSignatures, hideApproverSigning } from '../../actions/workOrderActions';
import { completeJobWithFinalCheck, updateJob } from '../../actions/manageJobActions';
import {
    buildCustomerContactOptions,
    contactDisplayName,
    resolveCustomerSelection,
    OTHER_CONTACT_VALUE
} from './signatureContactUtil';

interface Iprops {
    t: TFunction;
    toggleModal: () => void;
    toggleIsJobClosingWithSignature: () => void;
    submitSignature: typeof submitJobSignature;
    completeJobWithFinalCheck: typeof completeJobWithFinalCheck;
    toggleModalSignaturePad: typeof ItoggleModalSignaturePad;
    toggleIsCollectingSignatures: typeof toggleIsCollectingSignatures;
    hideApproverSigning?: typeof hideApproverSigning;
    updateJob: typeof updateJob;
    loading: boolean;
    installBasesPopulated: IinstallBasePopulated[];
    facilityContacts: IfacilityContact[];
    selectedJob: Ijob;
    selectedJobSignatures: IjobSignature[];
    selectedJobCoverLetter?: string;
    defaultCoverLetter: string;
    validateParts: boolean;
    isCollectingSignatures: boolean;
    isJobClosingWithSignature: boolean;
    isApproverSigning?: boolean;
    user: Iuser;
}

interface Istate {
    customerContactID: string;
    customerName: string;
    editorState: string | EditorState;
    signatures: { [key: string]: IjobSignature };
}

const canvasDimensions = {
    width: 468,
    height: 200
};

const prevSignatureWrap = {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center'
};

class SignaturePad extends React.Component<Iprops, Istate> {
    private formData: IsignatureFormData = new FormData();
    private customerSignaturePad: any = {};
    private serviceEngineerSignaturePad: any = {};
    private approverSignaturePad: any = {};

    constructor(props: any) {
        super(props);
        this.state = {
            customerContactID: '',
            customerName: '',
            editorState: this.props.selectedJobCoverLetter ?? this.props.defaultCoverLetter,
            signatures: {}
        };
        this.customerSignaturePad = React.createRef();
        this.serviceEngineerSignaturePad = React.createRef();
        this.approverSignaturePad = React.createRef();
    }

    componentDidMount() {
        this.getSignatures(this.props.selectedJobSignatures).then(signatures => {
            const { contactID, customerName } = resolveCustomerSelection(
                signatures.Customer,
                this.props.facilityContacts
            );
            this.setState({ signatures, customerContactID: contactID, customerName });
        });
    }

    onChange = (editorState: EditorState) => {
        this.setState({
            editorState
        });
    };

    handleInputChange(event: any) {
        const { value } = event.currentTarget;

        this.setState({ customerName: value });
    }

    handleContactChange = (option: { value: string; label: string } | null) => {
        if (!option) {
            this.setState({ customerContactID: '', customerName: '' });
            return;
        }
        if (option.value === OTHER_CONTACT_VALUE) {
            this.setState({ customerContactID: OTHER_CONTACT_VALUE, customerName: '' });
            return;
        }
        const contact = this.props.facilityContacts.find(c => c.id === option.value);
        if (!contact) {
            throw new Error(`Facility contact ${option.value} not found`);
        }
        this.setState({ customerContactID: contact.id, customerName: contactDisplayName(contact) });
    };

    areSignaturesAvailable = (): { [key: string]: boolean } => {
        const signatures = this.state.signatures as { [key: string]: IjobSignature };
        const availableSignatures = [signatures.Customer, signatures.FSE];
        const all = availableSignatures.every(sig => Boolean(sig?.uri));
        const some = availableSignatures.some(sig => Boolean(sig?.uri));

        return {
            all,
            some
        };
    };

    areSummissionsValid(): { [key: string]: boolean } {
        const summissions = [
            (this.customerSignaturePad as SignatureCanvas)?.isEmpty(),
            (this.serviceEngineerSignaturePad as SignatureCanvas)?.isEmpty()
        ];
        const all = summissions.every((sub: boolean) => sub === false);
        const some = summissions.some((sub: boolean) => sub === false);
        const existingSignatures = !isEmpty(this.state.signatures);
        const nameOnly = Boolean(this.state.customerName) && !existingSignatures && !some;

        return {
            all,
            some,
            existingSignatures,
            nameOnly
        };
    }

    isValidSubmission(): { msg: string; valid: boolean } {
        if (
            this.props.selectedJob.jobTypeID === jobTypesIdEnum.verification &&
            this.state.editorState === this.props.defaultCoverLetter &&
            !this.props.selectedJobCoverLetter
        ) {
            return { msg: 'Make sure to fill out the cover letter', valid: false };
        }

        if (this.state.customerName === '') {
            return { msg: 'Customer name is required', valid: false };
        }

        const isValid = this.areSignaturesAvailable().some || this.areSummissionsValid().all;

        return { msg: 'Missing Form Data', valid: isValid ?? false };
    }

    submitApproverSignature = () => {
        // MMG-4163 made the approver pad render the saved signature as an image
        // (no canvas) when one already exists. In that case the canvas ref is
        // null and there is nothing to submit - the approver step is already
        // satisfied, so close out instead of demanding a fresh drawing.
        if (this.state.signatures.Approver?.uri) {
            this.cancel();
            return;
        }

        const approverCanvas = this.approverSignaturePad as SignatureCanvas;
        if (!approverCanvas || approverCanvas.isEmpty()) {
            toastr.error('Approver signature is required', '', constants.toastrError);
            return;
        }

        approverCanvas.getTrimmedCanvas().toBlob(blob => {
            if (blob) {
                this.props.submitSignature(
                    this.props.t,
                    this.props.selectedJob.id,
                    blob,
                    approverCanvas.toDataURL(),
                    SignatureTypeEnum.Approver,
                    `${this.props.user.first} ${this.props.user.last}`,
                    true,
                    this.props.installBasesPopulated
                );
            }
        });
    };

    submit = () => {
        if (this.props.isApproverSigning) {
            this.submitApproverSignature();
            return;
        }

        const validationCheck = this.isValidSubmission();

        if (!validationCheck.valid) {
            toastr.error(validationCheck.msg, '', constants.toastrError);

            return;
        }

        const customerCanvas = this.customerSignaturePad as SignatureCanvas;
        const engineerCanvas = this.serviceEngineerSignaturePad as SignatureCanvas;
        const hasSignature: { [key: string]: boolean } = { customer: false, engineer: false };
        const signatureUri: { [key: string]: string | undefined } = { customer: undefined, engineer: undefined };

        // check if the current signature we are looking at was previously created and is in state
        if (this.state.signatures !== undefined && Object.keys(this.state.signatures).length > 0) {
            if (this.state.signatures.Customer !== undefined) {
                signatureUri.customer = this.state.signatures.Customer.uri;
                hasSignature.customer = true;
            }

            if (this.state.signatures.FSE !== undefined) {
                signatureUri.engineer = this.state.signatures.FSE.uri;
                hasSignature.engineer = true;
            }
        }

        if (this.props.selectedJob.jobTypeID === jobTypesIdEnum.verification) {
            this.props.updateJob({
                ...this.props.selectedJob,
                coverLetter: this.state.editorState as string
            });
        }

        if (
            !this.props.isCollectingSignatures &&
            this.props.isJobClosingWithSignature &&
            this.areSignaturesAvailable().all &&
            !this.areSummissionsValid().all
        ) {
            this.cancel();
            this.props.completeJobWithFinalCheck();
        } else if (this.areSummissionsValid().some) {
            customerCanvas?.getTrimmedCanvas().toBlob(blob => {
                if (blob && !customerCanvas?.isEmpty()) {
                    this.props.submitSignature(
                        this.props.t,
                        this.props.selectedJob.id,
                        blob,
                        customerCanvas?.toDataURL(),
                        SignatureTypeEnum.Customer,
                        this.state.customerName,
                        false,
                        undefined,
                        undefined,
                        this.state.customerContactID === OTHER_CONTACT_VALUE
                            ? undefined
                            : this.state.customerContactID || undefined
                    );
                }
            });

            engineerCanvas?.getTrimmedCanvas().toBlob(blob => {
                if (blob && !engineerCanvas?.isEmpty()) {
                    this.props.submitSignature(
                        this.props.t,
                        this.props.selectedJob.id,
                        blob,
                        engineerCanvas?.toDataURL(),
                        SignatureTypeEnum.FSE,
                        `${this.props.user.first} ${this.props.user.last}`,
                        true,
                        this.props.installBasesPopulated
                    );
                }
            });
        } else {
            this.cancel();
        }
    };

    clear = () => {
        this.setState({
            customerContactID: '',
            customerName: '',
            editorState: this.props.selectedJobCoverLetter ?? this.props.defaultCoverLetter,
            signatures: {}
        });
        this.customerSignaturePad?.clear();
        this.serviceEngineerSignaturePad?.clear();
    };

    cancel = () => {
        if (this.props.isJobClosingWithSignature) {
            this.props.toggleIsJobClosingWithSignature();
        }
        if (this.props.isCollectingSignatures) {
            this.props.toggleIsCollectingSignatures();
        }
        if (this.props.isApproverSigning) {
            this.props.hideApproverSigning?.();
        }

        this.props.toggleModal();
    };

    getSignatures = async (selectedJobSignatures: IjobSignature[]) => {
        const withURI = await selectedJobSignatures.map(async signature => {
            const uri = await localForage.getItem<string>(signature.id);

            return { ...signature, uri: uri || undefined } as IjobSignature;
        });
        const signatuesWithImgs = await Promise.all(withURI);
        const byType = keyBy(
            signatuesWithImgs,
            (signature: IjobSignature) => SignatureTypeEnum[signature.signatureType]
        ) as { [key: string]: IjobSignature };

        return byType;
    };

    render() {
        const { loading, selectedJob, t, user, isApproverSigning } = this.props;
        const isVerificationJob = selectedJob.jobTypeID === jobTypesIdEnum.verification;
        const signatures = this.state.signatures as { [key: string]: IjobSignature };
        const fseBrand = user.brand ? user.brand.name : 'BeaconMedaes LLC';
        const contactOptions = buildCustomerContactOptions(this.props.facilityContacts, t('common:other'));
        const selectedContactOption =
            contactOptions.find(option => option.value === this.state.customerContactID) ?? null;

        if (isApproverSigning) {
            return (
                <div className="beacon-form">
                    <Col xs={12}>
                        <FormGroup>
                            <FormLabel className="control-label">
                                {String(t('customerConfirmationSignature'))}
                                <RequiredIndicator meta={{ required: true }} />
                            </FormLabel>
                            <div className="signaturePad">
                                {signatures.Customer?.uri ? (
                                    <div style={{ ...canvasDimensions, ...prevSignatureWrap }}>
                                        <img src={signatures.Customer.uri} alt="" />
                                    </div>
                                ) : (
                                    <SignatureCanvas
                                        ref={ref => {
                                            this.customerSignaturePad = ref;
                                        }}
                                        penColor="#333333"
                                        canvasProps={canvasDimensions}
                                    />
                                )}
                            </div>
                        </FormGroup>
                    </Col>
                    <Col xs={12}>
                        <FormGroup>
                            <FormLabel className="control-label">
                                {String(t('serviceEngineerConfirmationSignature'))}
                                <RequiredIndicator meta={{ required: true }} />
                            </FormLabel>
                            <div className="signaturePad">
                                {signatures.FSE?.uri ? (
                                    <div style={{ ...canvasDimensions, ...prevSignatureWrap }}>
                                        <img src={signatures.FSE.uri} alt="" />
                                    </div>
                                ) : (
                                    <SignatureCanvas
                                        ref={ref => {
                                            this.serviceEngineerSignaturePad = ref;
                                        }}
                                        penColor="#333333"
                                        canvasProps={canvasDimensions}
                                    />
                                )}
                            </div>
                        </FormGroup>
                    </Col>
                    <Col xs={12}>
                        <FormGroup>
                            <FormLabel className="control-label">
                                {String(t('approverSignatureLabel') || 'Approver Signature')}
                                <RequiredIndicator meta={{ required: true }} />
                            </FormLabel>
                            <div className="signaturePad">
                                {signatures.Approver?.uri ? (
                                    <div style={{ ...canvasDimensions, ...prevSignatureWrap }}>
                                        <img src={signatures.Approver.uri} alt="" />
                                    </div>
                                ) : (
                                    <SignatureCanvas
                                        ref={ref => {
                                            this.approverSignaturePad = ref;
                                        }}
                                        penColor="#333333"
                                        canvasProps={canvasDimensions}
                                    />
                                )}
                            </div>
                        </FormGroup>
                    </Col>
                    <Col xs={12} className="form-buttons d-flex justify-content-between gap-2">
                        <div>
                            <Button variant="default" type="button" onClick={this.cancel}>
                                {String(t('common:cancel'))}
                            </Button>
                            <Button
                                variant="default"
                                type="button"
                                onClick={() => this.approverSignaturePad?.clear?.()}
                                style={{ marginLeft: '1rem' }}
                            >
                                {String(t('clearSignature'))}
                            </Button>
                        </div>
                        <Button variant="default" disabled={loading} onClick={this.submit}>
                            {String(t('common:save'))}
                        </Button>
                    </Col>
                </div>
            );
        }

        return (
            <div className="beacon-form">
                {isVerificationJob && (
                    <Col xs={12}>
                        <FormGroup>
                            <FormLabel className="control-label">
                                {String(t('coverLetter'))}
                                <RequiredIndicator meta={{ required: true }} />
                            </FormLabel>
                            <RichTextEditor
                                onChange={this.onChange}
                                initialContent={this.props.defaultCoverLetter}
                                readOnly={false}
                                charLimit={1000}
                            />
                            <FormControl.Feedback />
                        </FormGroup>
                    </Col>
                )}
                <Col xs={12}>
                    <FormGroup>
                        <FormLabel className="control-label">
                            {String(t('authorizedCustomerName'))}
                            <RequiredIndicator meta={{ required: true }} />
                        </FormLabel>
                        <Select
                            classNamePrefix="react-select"
                            options={contactOptions}
                            value={selectedContactOption}
                            onChange={this.handleContactChange}
                            isClearable={true}
                        />
                        {this.state.customerContactID === OTHER_CONTACT_VALUE && (
                            <FormControl
                                onChange={event => {
                                    this.handleInputChange(event);
                                }}
                                type="text"
                                value={this.state.customerName}
                                style={{ marginTop: '0.5rem' }}
                            ></FormControl>
                        )}
                    </FormGroup>
                </Col>

                <Col xs={12}>
                    <FormGroup>
                        <FormLabel className="control-label">
                            {String(t('customerConfirmationSignature'))}
                            <RequiredIndicator meta={{ required: true }} />
                        </FormLabel>
                        <p>
                            I acknowledge that {fseBrand} is on-site working with, or around, the medical gas pipeline
                            system and have communicated any potential risks that their work may affect.
                        </p>
                        <div className="signaturePad">
                            {signatures.Customer?.uri ? (
                                <div
                                    style={{
                                        ...canvasDimensions,
                                        ...prevSignatureWrap
                                    }}
                                >
                                    <img src={signatures.Customer.uri} alt="" />
                                </div>
                            ) : (
                                <SignatureCanvas
                                    ref={ref => {
                                        this.customerSignaturePad = ref;
                                    }}
                                    penColor="#333333"
                                    canvasProps={canvasDimensions}
                                />
                            )}
                        </div>
                    </FormGroup>
                </Col>
                <Col xs={12}>
                    <FormGroup>
                        <FormLabel className="control-label">
                            {String(t('serviceEngineerConfirmationSignature'))}
                            <RequiredIndicator meta={{ required: true }} />
                        </FormLabel>
                        <div className="signaturePad">
                            {signatures.FSE?.uri ? (
                                <div
                                    style={{
                                        ...canvasDimensions,
                                        ...prevSignatureWrap
                                    }}
                                >
                                    <img src={signatures.FSE.uri} alt="" />
                                </div>
                            ) : (
                                <SignatureCanvas
                                    ref={ref => {
                                        this.serviceEngineerSignaturePad = ref;
                                    }}
                                    penColor="#333333"
                                    canvasProps={canvasDimensions}
                                />
                            )}
                        </div>
                    </FormGroup>
                </Col>

                <Col xs={12} className="form-buttons d-flex justify-content-between gap-2">
                    <div>
                        <Button variant="default" type="button" onClick={this.cancel}>
                            {String(t('common:cancel'))}
                        </Button>
                        <Button
                            variant="default"
                            type="button"
                            onClick={this.clear}
                            style={{
                                marginLeft: '1rem'
                            }}
                        >
                            {String(t('clearSignature'))}
                        </Button>
                    </div>
                    <Button variant="default" disabled={loading} onClick={this.submit}>
                        {String(t('common:save'))}
                    </Button>
                </Col>
            </div>
        );
    }
}

export default SignaturePad;
